import { supabase } from './supabase';

// Web Push client. Flow:
//   1. Ensure service worker is registered (main.tsx already does this in prod).
//   2. Ask for Notification permission.
//   3. Subscribe via PushManager with our VAPID public key.
//   4. Store endpoint + keys in web_push_subscriptions so the edge function
//      can fan out urgent messages.
//
// iOS specifics: requires iOS 16.4+ AND the PWA must be installed via A2HS.
// In a regular Safari tab, subscribe() throws. We gate on standalone mode.

const VAPID_PUBLIC = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

function inStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const ios = (navigator as any).standalone === true;
  const mq =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  return ios || mq;
}

export type WebPushCapability =
  | 'ready' // can subscribe now
  | 'needs-install' // in Safari tab on iOS — must A2HS first
  | 'needs-permission' // in standalone but Notification.permission !== 'granted'
  | 'unsupported' // browser has no Push API
  | 'no-vapid'; // VITE_VAPID_PUBLIC_KEY not configured

export function pushCapability(): WebPushCapability {
  if (!VAPID_PUBLIC) return 'no-vapid';
  if (typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (!inStandalone()) return 'needs-install';
  if (Notification.permission === 'granted') return 'ready';
  return 'needs-permission';
}

// Convert a base64url VAPID key to the Uint8Array that PushManager expects.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64(buf: ArrayBuffer | null): string {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export async function enableWebPush(userId: string): Promise<void> {
  if (!VAPID_PUBLIC) throw new Error('VITE_VAPID_PUBLIC_KEY not configured');
  if (!inStandalone()) {
    throw new Error('Install the app first (Add to Home Screen), then enable notifications.');
  }

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    throw new Error('Notification permission denied.');
  }

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  // TS strict disagrees with the DOM lib about Uint8Array generic, so cast.
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC) as BufferSource,
    }));

  const p256dh = arrayBufferToBase64(sub.getKey('p256dh'));
  const authKey = arrayBufferToBase64(sub.getKey('auth'));

  const { error } = await supabase.from('web_push_subscriptions').upsert(
    {
      user_id: userId,
      endpoint: sub.endpoint,
      p256dh,
      auth: authKey,
      last_used_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,endpoint' }
  );
  if (error) throw error;
}

export async function disableWebPush(userId: string): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    try {
      await sub.unsubscribe();
    } catch {}
    await supabase
      .from('web_push_subscriptions')
      .delete()
      .eq('user_id', userId)
      .eq('endpoint', sub.endpoint);
  }
}

export async function isSubscribed(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  const sub = await reg?.pushManager.getSubscription();
  return !!sub;
}
